import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { invalidateAccountsQuery } from "hooks/useAccountsTable";
import ConstantContactAccountsService from "services/ConstantContactAccounts";

const AUTHENTICATED = "authenticated";
const PENDING = "pending";
const UNAUTHENTICATED = "unauthenticated";
const EXPIRED = "expired";

const POLL_INTERVAL_MS = 2000;

export default function AccountAuthenticationModal({
  account,
  open,
  onOpenChange,
}) {
  const [clicked, setClicked] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const startAuth = useMutation({
    mutationFn: () => ConstantContactAccountsService.getAuthURL(account.id),
    onError: () =>
      toast.error("Could not start authentication with Constant Contact"),
  });

  const { data: polled } = useQuery({
    queryKey: ["cc-account", account.id, attempt],
    queryFn: () => ConstantContactAccountsService.get(account.id),
    enabled: open && clicked,
    refetchInterval: query =>
      query.state.data?.authentication_status === PENDING
        ? POLL_INTERVAL_MS
        : false,
  });

  const status = clicked
    ? (polled?.authentication_status ?? PENDING)
    : account.authentication_status;

  const handleOpenChange = next => {
    onOpenChange(next);
    if (!next) {
      setClicked(false);
      invalidateAccountsQuery();
    }
  };

  const handleAuthenticate = async () => {
    const authWindow = window.open("", "_blank");
    try {
      const url = await startAuth.mutateAsync();
      if (authWindow) {
        authWindow.opener = null;
        authWindow.location.href = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      setAttempt(a => a + 1);
      setClicked(true);
    } catch {
      authWindow?.close();
    }
  };

  const renderContent = () => {
    if (!clicked) {
      return (
        <Button disabled={startAuth.isPending} onClick={handleAuthenticate}>
          Authenticate with Constant Contact
        </Button>
      );
    }
    if (status === AUTHENTICATED) return <p>Successfully Authenticated</p>;
    if (status === UNAUTHENTICATED || status === EXPIRED) {
      const reason = polled?.last_refresh_error;
      return <p>Authentication Failed{reason ? `: ${reason}` : ""}</p>;
    }
    return <p>Authenticating…</p>;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogTitle>Authenticate Account</DialogTitle>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
